import { useMemo, useState, useEffect, useRef } from 'react';
import { useUserStore } from '@/stores/userStore';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { getAvailableMailDomains, getDefaultMailDomain, startSahastaMailOAuth } from '@/lib/mailBridge';
import { Calendar, Clock, Zap, Save, Link, CheckCircle, Plus, Trash2, ShieldAlert, Plane, BriefcaseBusiness, Moon, Palette, Bell, Smartphone, Github, Mail, ShieldCheck, Cloud } from 'lucide-react';

interface SchedulingConstraint {
  id: number;
  constraint_type: 'work_hours' | 'blocked_time' | 'preference' | 'break';
  days_of_week: number[];
  start_time: string;
  end_time: string;
  priority: number;
  is_active: boolean;
  metadata?: Record<string, unknown>;
}

interface LinkedAccountEmail {
  email: string;
  provider: string;
  provider_domain?: string | null;
  display_name?: string | null;
  is_primary?: boolean | number;
  mail_role?: string | null;
  can_create_mail_accounts?: boolean | number;
}

interface EmailVerificationPreview {
  email: string;
  current_email: string;
  expires_at: string;
}

interface PendingEmailVerification {
  email: string;
  requested_at: string;
  expires_at: string;
}

const WEEK_DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

const ACCENT_COLORS = ['#13D8BD', '#FF4FD8', '#8CFF7A', '#FFD166', '#EF4444', '#070A12'];
const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
];

function dateRangeFromInputs(start: string, end: string): { start: string; end: string } | null {
  if (!start) return null;
  const rangeEnd = end || start;
  return start <= rangeEnd ? { start, end: rangeEnd } : { start: rangeEnd, end: start };
}

function daysOfWeekForDateRange(start: string, end: string): number[] {
  const range = dateRangeFromInputs(start, end);
  if (!range) return [];
  const days = new Set<number>();
  const [startYear, startMonth, startDay] = range.start.split('-').map(Number);
  const [endYear, endMonth, endDay] = range.end.split('-').map(Number);
  if (!startYear || !startMonth || !startDay || !endYear || !endMonth || !endDay) return [];

  let cursor = new Date(startYear, startMonth - 1, startDay, 12, 0, 0, 0);
  const endDate = new Date(endYear, endMonth - 1, endDay, 12, 0, 0, 0);
  while (cursor <= endDate) {
    days.add(cursor.getDay());
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1, 12, 0, 0, 0);
  }
  return [...days].sort((a, b) => a - b);
}

function formatConstraintDateRange(constraint: SchedulingConstraint): string {
  const start = String(constraint.metadata?.start_date || constraint.metadata?.date || 'Date not set');
  const end = String(constraint.metadata?.end_date || constraint.metadata?.date || start);
  return start === end ? start : `${start} to ${end}`;
}

function formatVacationConstraintRange(constraint: SchedulingConstraint): string {
  const startDate = String(constraint.metadata?.start_date || constraint.metadata?.date || 'Date not set');
  const endDate = String(constraint.metadata?.end_date || constraint.metadata?.date || startDate);
  const startTime = String(constraint.metadata?.start_time || constraint.start_time || '00:00');
  const endTime = String(constraint.metadata?.end_time || constraint.end_time || '24:00');
  const isAllDay = startTime === '00:00' && (endTime === '24:00' || endTime === '23:59');
  if (isAllDay) return startDate === endDate ? startDate : `${startDate} to ${endDate}`;
  return startDate === endDate
    ? `${startDate} ${startTime} - ${endTime}`
    : `${startDate} ${startTime} - ${endDate} ${endTime}`;
}

function formatConstraintDays(days: number[]): string {
  if (days.length === 7) return 'Every day';
  const ordered = WEEK_DAYS.filter(day => days.includes(day.value)).map(day => day.label);
  return ordered.length > 0 ? ordered.join(', ') : 'No days';
}

export function Settings() {
  const { toast } = useToast();
  const { linkOAuthProvider } = useAuth();
  const mailDomains = useMemo(() => getAvailableMailDomains(), []);
  const settings = useUserStore(s => s.settings);
  const primaryEmail = useUserStore(s => s.email);
  const userTimezone = useUserStore(s => s.timezone);
  const updateSettings = useUserStore(s => s.updateSettings);

  const [workStart, setWorkStart] = useState(settings.workHours?.start || '09:00');
  const [workEnd, setWorkEnd] = useState(settings.workHours?.end || '17:00');
  const [theme, setTheme] = useState(settings.theme || 'system');
  const [accentColor, setAccentColor] = useState(settings.accentColor || '#13D8BD');
  const [calendarDensity, setCalendarDensity] = useState(settings.calendarDensity || 'balanced');
  const [enableNotifications, setEnableNotifications] = useState(settings.enableNotifications ?? true);
  const [soundEnabled, setSoundEnabled] = useState(settings.soundEnabled ?? true);
  const [workDays, setWorkDays] = useState<number[]>(
    settings.workDays?.length ? settings.workDays : [1, 2, 3, 4, 5]
  );
  const [timezone, setTimezone] = useState(
    userTimezone || (settings as any).timezone || detectBrowserTimezone() || 'UTC'
  );
  const [settingsSaveState, setSettingsSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autosaveReadyRef = useRef(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [calendars, setCalendars] = useState<Array<{ id: string; summary: string; primary?: boolean; color?: string | null; selected?: boolean }>>([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const [constraints, setConstraints] = useState<SchedulingConstraint[]>([]);
  const [breakStart, setBreakStart] = useState('12:00');
  const [breakEnd, setBreakEnd] = useState('13:00');
  const [breakDays, setBreakDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [breakPermeability, setBreakPermeability] = useState<'semi' | 'non'>('non');
  const [savingBreak, setSavingBreak] = useState(false);
  const [sleepStart, setSleepStart] = useState('23:00');
  const [sleepEnd, setSleepEnd] = useState('07:00');
  const [sleepDays, setSleepDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [savingSleep, setSavingSleep] = useState(false);
  const [vacationStartDate, setVacationStartDate] = useState('');
  const [vacationEndDate, setVacationEndDate] = useState('');
  const [vacationStartTime, setVacationStartTime] = useState('00:00');
  const [vacationEndTime, setVacationEndTime] = useState('23:59');
  const [vacationLabel, setVacationLabel] = useState('');
  const [savingVacation, setSavingVacation] = useState(false);
  const [overrideStartDate, setOverrideStartDate] = useState('');
  const [overrideEndDate, setOverrideEndDate] = useState('');
  const [overrideStartTime, setOverrideStartTime] = useState(workStart);
  const [overrideEndTime, setOverrideEndTime] = useState(workEnd);
  const [overrideLabel, setOverrideLabel] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [linkedEmails, setLinkedEmails] = useState<LinkedAccountEmail[]>([]);
  const [pendingEmails, setPendingEmails] = useState<PendingEmailVerification[]>([]);
  const [linkEmailInput, setLinkEmailInput] = useState('');
  const [mailLinkDomain, setMailLinkDomain] = useState(getDefaultMailDomain());
  const [linkingEmail, setLinkingEmail] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [pendingVerificationToken, setPendingVerificationToken] = useState('');
  const [verificationPreview, setVerificationPreview] = useState<EmailVerificationPreview | null>(null);
  const [verifyingEmail, setVerifyingEmail] = useState(false);

  useEffect(() => {
    checkCalendarStatus();
    loadConstraints();
    loadLinkedAccounts();
    previewEmailVerificationFromUrl();
  }, []);

  useEffect(() => {
    const detectedTimezone = detectBrowserTimezone();
    if (detectedTimezone && (!userTimezone || userTimezone === 'UTC') && timezone === 'UTC') {
      setTimezone(detectedTimezone);
    }
  }, []);

  useEffect(() => {
    if (!autosaveReadyRef.current) {
      autosaveReadyRef.current = true;
      return;
    }
    if (workDays.length === 0) {
      setSettingsSaveState('error');
      return;
    }
    setSettingsSaveState('saving');
    const timeout = window.setTimeout(() => {
      void persistSettings(false);
    }, 700);
    return () => window.clearTimeout(timeout);
  }, [
    workStart,
    workEnd,
    theme,
    accentColor,
    calendarDensity,
    enableNotifications,
    soundEnabled,
    timezone,
    workDays.join(','),
  ]);

  const loadLinkedAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await api.user.getEmails();
      if (response.success && response.data?.emails) {
        setLinkedEmails(response.data.emails);
        setPendingEmails(response.data.pending || []);
      }
    } catch (error) {
      console.error('Failed to load linked emails:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const previewEmailVerificationFromUrl = async () => {
    const token = new URLSearchParams(window.location.search).get('verify_email');
    if (!token) return;
    setPendingVerificationToken(token);
    const response = await api.user.previewEmailVerification({ token });
    if (response.success) {
      setVerificationPreview(response.data);
    } else {
      clearVerificationUrl();
      setPendingVerificationToken('');
      toast({
        title: 'Email verification failed',
        description: response.error?.message || 'Request a new verification link.',
        variant: 'destructive',
      });
    }
  };

  const clearVerificationUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('verify_email');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const confirmEmailVerification = async () => {
    if (!pendingVerificationToken) return;
    setVerifyingEmail(true);
    try {
      const response = await api.user.verifyEmail({ token: pendingVerificationToken });
      if (!response.success) throw new Error(response.error?.message || 'Could not verify email');
      clearVerificationUrl();
      setVerificationPreview(null);
      setPendingVerificationToken('');
      await loadLinkedAccounts();
      toast({
        title: 'Email linked',
        description: `${response.data?.email || verificationPreview?.email || 'Email'} can now sign in to this Fluxiplanner account.`,
      });
    } catch (error) {
      toast({
        title: 'Email verification failed',
        description: error instanceof Error ? error.message : 'Request a new verification link.',
        variant: 'destructive',
      });
    } finally {
      setVerifyingEmail(false);
    }
  };

  const cancelEmailVerification = () => {
    clearVerificationUrl();
    setVerificationPreview(null);
    setPendingVerificationToken('');
  };

  const requestEmailLink = async () => {
    const email = linkEmailInput.trim();
    if (!email) {
      toast({
        title: 'Enter an email',
        description: 'Add the email address you want linked to this Fluxiplanner account.',
        variant: 'destructive',
      });
      return;
    }

    setLinkingEmail(true);
    try {
      const response = await api.user.linkEmail({ email });
      if (!response.success) throw new Error(response.error?.message || 'Could not send verification email');
      setLinkEmailInput('');
      await loadLinkedAccounts();
      toast({
        title: response.data?.sent ? 'Verification email sent' : 'Verification link created',
        description: response.data?.sent
          ? `Open the email sent to ${response.data.email} and confirm the link in Fluxiplanner.`
          : response.data?.message || 'Email sending is not configured for this deployment.',
      });
    } catch (error) {
      toast({
        title: 'Could not link email',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLinkingEmail(false);
    }
  };

  const linkGithub = async () => {
    try {
      await linkOAuthProvider('github', '/settings');
    } catch (error) {
      toast({
        title: 'GitHub linking unavailable',
        description: error instanceof Error ? error.message : 'Check GitHub OAuth configuration.',
        variant: 'destructive',
      });
    }
  };

  const linkZoho = async () => {
    try {
      await linkOAuthProvider('zoho', '/settings');
    } catch (error) {
      toast({
        title: 'Zoho linking unavailable',
        description: error instanceof Error ? error.message : 'Check Zoho OAuth configuration.',
        variant: 'destructive',
      });
    }
  };

  const linkMail = () => {
    try {
      startSahastaMailOAuth(mailLinkDomain, '/settings');
    } catch (error) {
      toast({
        title: 'Mail linking unavailable',
        description: error instanceof Error ? error.message : 'Choose a configured Mail-app domain.',
        variant: 'destructive',
      });
    }
  };

  const checkCalendarStatus = async () => {
    try {
      const response = await api.calendar.getStatus();
      if (response.success && response.data) {
        setCalendarConnected(response.data.connected || false);
        if (response.data.connected) {
          void loadCalendars();
        }
      }
    } catch (error) {
      console.error('Failed to check calendar status:', error);
    }
  };

  const loadConstraints = async () => {
    try {
      const response = await api.user.getConstraints();
      if (response.success && response.data?.constraints) {
        setConstraints(response.data.constraints);
      }
    } catch (error) {
      console.error('Failed to load constraints:', error);
    }
  };

  const addBreak = async () => {
    if (breakDays.length === 0) {
      toast({
        title: 'Choose break days',
        description: 'Select at least one day for this break.',
        variant: 'destructive',
      });
      return;
    }
    if (breakEnd <= breakStart) {
      toast({
        title: 'Check the break time',
        description: 'Break end must be after break start.',
        variant: 'destructive',
      });
      return;
    }

    setSavingBreak(true);
    try {
      const response = await api.user.addConstraint({
        constraint_type: 'break',
        days_of_week: breakDays,
        start_time: breakStart,
        end_time: breakEnd,
        priority: breakPermeability === 'non' ? 9 : 5,
        metadata: { permeability: breakPermeability },
      });
      if (!response.success) throw new Error(response.error?.message || 'Failed to add break');
      await loadConstraints();
      toast({
        title: breakPermeability === 'semi' ? 'Semi-permeable break added' : 'Non-permeable break added',
        description:
          breakPermeability === 'semi'
            ? 'The scheduler may use this break only after working time and outside-hours options fail.'
            : 'The scheduler will treat this break as a last resort after every other infringement option.',
      });
    } catch (error) {
      toast({
        title: 'Could not add break',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingBreak(false);
    }
  };

  const addSleepSchedule = async () => {
    if (sleepDays.length === 0) {
      toast({
        title: 'Choose sleep days',
        description: 'Sleep protection needs at least one day.',
        variant: 'destructive',
      });
      return;
    }

    setSavingSleep(true);
    try {
      const response = await api.user.addConstraint({
        constraint_type: 'blocked_time',
        days_of_week: sleepDays,
        start_time: sleepStart,
        end_time: sleepEnd,
        priority: 1000,
        metadata: {
          kind: 'sleep',
          label: 'Sleep',
          permeability: 'non',
        },
      });
      if (!response.success) throw new Error(response.error?.message || 'Failed to add sleep protection');
      await loadConstraints();
      toast({
        title: 'Sleep protected',
        description: 'The scheduler will never place activities into this sleep window.',
      });
    } catch (error) {
      toast({
        title: 'Could not add sleep protection',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingSleep(false);
    }
  };

  const addVacationRange = async () => {
    const range = dateRangeFromInputs(vacationStartDate, vacationEndDate);
    if (!range) {
      toast({
        title: 'Choose a vacation start date',
        description: 'Pick the date or range that should be protected.',
        variant: 'destructive',
      });
      return;
    }
    if (range.start === range.end && vacationEndTime <= vacationStartTime) {
      toast({
        title: 'Check vacation times',
        description: 'For a same-day vacation window, the end time must be after the start time.',
        variant: 'destructive',
      });
      return;
    }

    setSavingVacation(true);
    try {
      const response = await api.user.addConstraint({
        constraint_type: 'blocked_time',
        days_of_week: daysOfWeekForDateRange(range.start, range.end),
        start_time: vacationStartTime,
        end_time: vacationEndTime,
        priority: 100,
        metadata: {
          kind: 'vacation',
          date: range.start,
          start_date: range.start,
          end_date: range.end,
          start_time: vacationStartTime,
          end_time: vacationEndTime,
          label: vacationLabel.trim() || (range.start === range.end ? 'Vacation day' : 'Vacation range'),
          permeability: 'non',
        },
      });
      if (!response.success) throw new Error(response.error?.message || 'Failed to add vacation range');
      await loadConstraints();
      setVacationStartDate('');
      setVacationEndDate('');
      setVacationStartTime('00:00');
      setVacationEndTime('23:59');
      setVacationLabel('');
      toast({
        title: 'Vacation time protected',
        description: 'The scheduler will move required work before this window and will not schedule into it.',
      });
    } catch (error) {
      toast({
        title: 'Could not add vacation range',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingVacation(false);
    }
  };

  const addOverrideWorkRange = async () => {
    const range = dateRangeFromInputs(overrideStartDate, overrideEndDate);
    if (!range) {
      toast({
        title: 'Choose an override start date',
        description: 'Pick the date or range that should become workable.',
        variant: 'destructive',
      });
      return;
    }
    if (overrideEndTime <= overrideStartTime) {
      toast({
        title: 'Check override hours',
        description: 'Override end time must be after start time.',
        variant: 'destructive',
      });
      return;
    }

    setSavingOverride(true);
    try {
      const response = await api.user.addConstraint({
        constraint_type: 'work_hours',
        days_of_week: daysOfWeekForDateRange(range.start, range.end),
        start_time: overrideStartTime,
        end_time: overrideEndTime,
        priority: 20,
        metadata: {
          kind: 'override_work_day',
          date: range.start,
          start_date: range.start,
          end_date: range.end,
          label: overrideLabel.trim() || (range.start === range.end ? 'Override work day' : 'Override work range'),
        },
      });
      if (!response.success) throw new Error(response.error?.message || 'Failed to add override work range');
      await loadConstraints();
      setOverrideStartDate('');
      setOverrideEndDate('');
      setOverrideLabel('');
      toast({
        title: 'Override work range added',
        description: 'The scheduler can use those dated hours even if the day is normally off.',
      });
    } catch (error) {
      toast({
        title: 'Could not add override work range',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingOverride(false);
    }
  };

  const deleteConstraint = async (id: number) => {
    try {
      const response = await api.user.deleteConstraint(id);
      if (!response.success) throw new Error(response.error?.message || 'Failed to delete constraint');
      setConstraints(current => current.filter(constraint => constraint.id !== id));
    } catch (error) {
      toast({
        title: 'Could not delete constraint',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const toggleWorkDay = (day: number, checked: boolean) => {
    setWorkDays(current => {
      if (checked) return Array.from(new Set([...current, day])).sort((a, b) => a - b);
      return current.filter(value => value !== day);
    });
  };

  const toggleSleepDay = (day: number, checked: boolean) => {
    setSleepDays(current => {
      if (checked) return Array.from(new Set([...current, day])).sort((a, b) => a - b);
      return current.filter(value => value !== day);
    });
  };

  const toggleBreakDay = (day: number, checked: boolean) => {
    setBreakDays(current => {
      if (checked) return Array.from(new Set([...current, day])).sort((a, b) => a - b);
      return current.filter(value => value !== day);
    });
  };

  const loadCalendars = async () => {
    try {
      const response = await api.calendar.listCalendars();
      if (response.success && response.data) {
        setCalendars(response.data);
        setSelectedCalendarIds(
          response.data.filter((calendar: any) => calendar.selected).map((calendar: any) => calendar.id)
        );
      }
    } catch (error) {
      console.error('Failed to load calendars:', error);
    }
  };

  const toggleCalendar = (calendarId: string, checked: boolean) => {
    setSelectedCalendarIds(current => {
      if (checked) return Array.from(new Set([...current, calendarId]));
      return current.filter(id => id !== calendarId);
    });
  };

  const saveCalendarSelection = async () => {
    if (selectedCalendarIds.length === 0) {
      toast({
        title: 'Select at least one calendar',
        description: 'Fluxiplanner needs at least one calendar for immovable constraints.',
        variant: 'destructive',
      });
      return;
    }

    setLoadingCalendar(true);
    try {
      const response = await api.calendar.updateCalendars(selectedCalendarIds);
      if (!response.success) throw new Error(response.error?.message || 'Failed to save calendars');
      await loadCalendars();
      const warningCount = Array.isArray(response.data?.warnings) ? response.data.warnings.length : 0;
      toast({
        title: warningCount ? 'Calendar constraints partially updated' : 'Calendar constraints updated',
        description: warningCount
          ? `${response.data?.synced ?? 0} events synced. ${warningCount} calendar${warningCount === 1 ? '' : 's'} could not be synced.`
          : `${response.data?.synced ?? 0} events synced from selected calendars.`,
      });
    } catch (error) {
      toast({
        title: 'Calendar selection failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCalendar(false);
    }
  };

  const persistSettings = async (showToast = true) => {
    if (workDays.length === 0) {
      if (showToast) {
        toast({
          title: 'Choose at least one working day',
          description: 'Use vacation days for fully protected dates.',
          variant: 'destructive',
        });
      }
      setSettingsSaveState('error');
      return;
    }

    try {
      const payload = {
        theme,
        accentColor,
        calendarDensity,
        enableNotifications,
        soundEnabled,
        workHours: {
          start: workStart,
          end: workEnd,
        },
        workDays,
        timezone,
      };
      updateSettings(payload);

      // Update timezone on user root
      const currentUser = useUserStore.getState();
      if (currentUser.userId) {
        useUserStore.getState().setUser({
          id: currentUser.userId,
          email: currentUser.email || '',
          displayName: currentUser.displayName || '',
          timezone,
        });
      }

      // Save to backend
      const response = await api.user.updateSettings(payload);
      if (!response.success) throw new Error(response.error?.message || 'Failed to save settings');
      setSettingsSaveState('saved');

      if (showToast) {
        toast({
          title: 'Settings saved',
          description: 'Your settings have been updated successfully.',
        });
      }
    } catch (error) {
      setSettingsSaveState('error');
      if (showToast) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to save settings. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleSaveSettings = () => {
    void persistSettings(true);
  };

  const handleNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast({
        title: 'Notifications unavailable',
        description: 'This browser does not expose notification permissions to Fluxiplanner.',
        variant: 'destructive',
      });
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setEnableNotifications(true);
      updateSettings({ enableNotifications: true });
      toast({
        title: 'Reminders enabled',
        description: 'Fluxiplanner can notify you about upcoming activities and logging prompts.',
      });
    }
  };

  const handleConnectCalendar = async () => {
    setLoadingCalendar(true);
    try {
      const response = await api.calendar.getAuthUrl();
      if (response.success && response.data) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to connect calendar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleSyncCalendar = async () => {
    setLoadingCalendar(true);
    try {
      const response = await api.calendar.sync();
      if (!response.success) throw new Error(response.error?.message || 'Calendar sync failed');
      const warningCount = Array.isArray(response.data?.warnings) ? response.data.warnings.length : 0;
      toast({
        title: warningCount > 0 ? 'Calendar partially synced' : 'Calendar synced',
        description:
          warningCount > 0
            ? `${response.data?.synced ?? 0} events synchronized. ${warningCount} calendar${warningCount === 1 ? '' : 's'} need attention.`
            : 'Your Google Calendar has been synchronized.',
      });
      await loadCalendars();
    } catch (error) {
      toast({
        title: 'Calendar sync failed',
        description: error instanceof Error ? error.message : 'Reload calendars or reconnect Google Calendar.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    setLoadingCalendar(true);
    try {
      const response = await api.calendar.revoke();
      if (response.success) {
        setCalendarConnected(false);
        setCalendars([]);
        setSelectedCalendarIds([]);
        toast({
          title: 'Calendar disconnected',
          description: 'Your Google Calendar has been disconnected.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect calendar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCalendar(false);
    }
  };

  return (
    <div className="container mx-auto max-w-4xl p-6">
      <div className="mb-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">Settings</h1>
            <p className="text-muted-foreground">Manage your account and preferences</p>
          </div>
          <Badge variant={settingsSaveState === 'error' ? 'destructive' : 'outline'} className="w-fit rounded-md">
            {settingsSaveState === 'saving'
              ? 'Autosaving...'
              : settingsSaveState === 'saved'
                ? 'Autosaved'
                : settingsSaveState === 'error'
                  ? 'Needs attention'
                  : 'Autosave on'}
          </Badge>
        </div>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Accounts
            </CardTitle>
            <CardDescription>
              Link emails and providers to one Fluxiplanner account. Any linked email can sign in with this account password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <div className="space-y-2">
                <Label htmlFor="linkEmail">Link another email</Label>
                <Input
                  id="linkEmail"
                  type="email"
                  value={linkEmailInput}
                  onChange={event => setLinkEmailInput(event.target.value)}
                  placeholder="person@example.com"
                  autoComplete="email"
                />
              </div>
              <div className="flex items-end">
                <Button className="w-full md:w-auto" onClick={requestEmailLink} disabled={linkingEmail}>
                  <Link className="mr-2 h-4 w-4" />
                  {linkingEmail ? 'Sending...' : 'Send link'}
                </Button>
              </div>
            </div>

            <div className="grid gap-3 lg:grid-cols-3">
              <div className="flex flex-col gap-3 rounded-md border bg-background p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Mail className="h-4 w-4" />
                    SahastaMail
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Authenticate a Mail-app identity and link it without waiting for a verification email.
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr_auto] lg:grid-cols-1">
                  <Select value={mailLinkDomain} onValueChange={setMailLinkDomain}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {mailDomains.map(domain => (
                        <SelectItem key={domain} value={domain}>
                          {domain}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="w-full" onClick={linkMail}>
                    <Mail className="mr-2 h-4 w-4" />
                    Link Mail
                  </Button>
                </div>
              </div>

              <div className="flex flex-col justify-between gap-3 rounded-md border bg-background p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Cloud className="h-4 w-4" />
                    Zoho
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Link Zoho so team members can accept invitations with their company Zoho account.
                  </p>
                </div>
                <Button variant="outline" className="w-full" onClick={linkZoho}>
                  <Cloud className="mr-2 h-4 w-4" />
                  Link Zoho
                </Button>
              </div>

              <div className="flex flex-col justify-between gap-3 rounded-md border bg-background p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Github className="h-4 w-4" />
                    GitHub
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Connect GitHub so you can sign in and attach repo work to this Fluxiplanner account.
                  </p>
                </div>
                <Button variant="outline" className="w-full" onClick={linkGithub}>
                  <Github className="mr-2 h-4 w-4" />
                  Link GitHub
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Linked emails</Label>
                <Button variant="ghost" size="sm" onClick={loadLinkedAccounts} disabled={loadingAccounts}>
                  Refresh
                </Button>
              </div>
              {linkedEmails.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {loadingAccounts ? 'Loading linked emails...' : 'No linked emails loaded yet.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {linkedEmails.map(identity => {
                    const isMailAdmin = Boolean(identity.can_create_mail_accounts);
                    return (
                      <div
                        key={`${identity.provider}:${identity.provider_domain || 'global'}:${identity.email}`}
                        className="flex min-h-12 items-center gap-3 rounded-md border bg-background p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-medium">{identity.email}</span>
                            {identity.is_primary ? <Badge variant="secondary">Primary</Badge> : null}
                            {identity.provider === 'github' ? <Badge variant="outline">GitHub</Badge> : null}
                            {identity.provider === 'mail' ? <Badge variant="outline">SahastaMail</Badge> : null}
                            {isMailAdmin ? (
                              <Badge className="gap-1 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-200">
                                <ShieldCheck className="h-3 w-3" />
                                Mail admin
                              </Badge>
                            ) : null}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {identity.provider_domain || providerLabel(identity.provider)}
                            {identity.mail_role ? ` | ${identity.mail_role}` : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {pendingEmails.length > 0 && (
                <div className="space-y-2">
                  <Label>Pending verification</Label>
                  {pendingEmails.map(pending => (
                    <div
                      key={`${pending.email}:${pending.expires_at}`}
                      className="flex min-h-12 items-center gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{pending.email}</div>
                        <div className="text-xs text-muted-foreground">
                          Expires {new Date(pending.expires_at).toLocaleString()}
                        </div>
                      </div>
                      <Badge variant="outline">Pending</Badge>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Mail admin emails can authorize delegated Mail-app account creation for their domain when that Mail session is active.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Appearance
            </CardTitle>
            <CardDescription>
              Tune Fluxiplanner so the calendar feels like your daily cockpit on desktop and mobile.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Theme</Label>
                <Select value={theme} onValueChange={value => setTheme(value as typeof theme)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Calendar density</Label>
                <Select
                  value={calendarDensity}
                  onValueChange={value => setCalendarDensity(value as typeof calendarDensity)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="balanced">Balanced</SelectItem>
                    <SelectItem value="spacious">Spacious</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="accentColor">Accent color</Label>
              <div className="flex flex-wrap items-center gap-2">
                {ACCENT_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Use accent ${color}`}
                    className="h-9 w-9 rounded-full border-2 shadow-sm transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    style={{
                      backgroundColor: color,
                      borderColor: accentColor.toUpperCase() === color ? 'hsl(var(--foreground))' : 'transparent',
                    }}
                    onClick={() => setAccentColor(color)}
                  />
                ))}
                <Input
                  id="accentColor"
                  className="max-w-36"
                  value={accentColor}
                  onChange={event => setAccentColor(event.target.value)}
                  placeholder="#13D8BD"
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex min-h-12 items-center gap-3 rounded-md border bg-background px-3 py-2">
                <Checkbox
                  checked={enableNotifications}
                  onCheckedChange={value => setEnableNotifications(Boolean(value))}
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Bell className="h-4 w-4" />
                    Activity reminders
                  </span>
                  <span className="block text-xs text-muted-foreground">Next activity and hydration prompts.</span>
                </span>
              </label>
              <label className="flex min-h-12 items-center gap-3 rounded-md border bg-background px-3 py-2">
                <Checkbox
                  checked={soundEnabled}
                  onCheckedChange={value => setSoundEnabled(Boolean(value))}
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Smartphone className="h-4 w-4" />
                    Native feel
                  </span>
                  <span className="block text-xs text-muted-foreground">Use app-style prompts and sounds where supported.</span>
                </span>
              </label>
            </div>
            {'Notification' in window && Notification.permission !== 'granted' && (
              <Button variant="outline" onClick={handleNotificationPermission}>
                <Bell className="mr-2 h-4 w-4" />
                Allow notification permission
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Working Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Working Hours
            </CardTitle>
            <CardDescription>
              Set your typical working hours for optimal activity scheduling
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="workStart">Start Time</Label>
                <Input
                  id="workStart"
                  type="time"
                  value={workStart}
                  onChange={e => setWorkStart(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="workEnd">End Time</Label>
                <Input
                  id="workEnd"
                  type="time"
                  value={workEnd}
                  onChange={e => setWorkEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Normal Working Days</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-7">
                {WEEK_DAYS.map(day => (
                  <label
                    key={day.value}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-md border bg-background px-3 text-sm"
                  >
                    <Checkbox
                      checked={workDays.includes(day.value)}
                      onCheckedChange={value => toggleWorkDay(day.value, Boolean(value))}
                    />
                    <span>{day.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Excluded days are outside normal work. Pressing due-date matters may still use them after safer options fail.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Moon className="h-5 w-5" />
              Sleep Protection
            </CardTitle>
            <CardDescription>
              Sleep is sacred. These windows are non-permeable and never used for scheduling.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
              <div className="space-y-2">
                <Label htmlFor="sleepStart">Sleep start</Label>
                <Input
                  id="sleepStart"
                  type="time"
                  value={sleepStart}
                  onChange={event => setSleepStart(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sleepEnd">Wake time</Label>
                <Input
                  id="sleepEnd"
                  type="time"
                  value={sleepEnd}
                  onChange={event => setSleepEnd(event.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button className="w-full md:w-auto" onClick={addSleepSchedule} disabled={savingSleep}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Sleep Days</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-7">
                {WEEK_DAYS.map(day => (
                  <label
                    key={day.value}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-md border bg-background px-3 text-sm"
                  >
                    <Checkbox
                      checked={sleepDays.includes(day.value)}
                      onCheckedChange={value => toggleSleepDay(day.value, Boolean(value))}
                    />
                    <span>{day.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Overnight sleep blocks protect the start day night and the next morning.
              </p>
            </div>

            <div className="space-y-2">
              {constraints.filter(
                constraint =>
                  constraint.constraint_type === 'blocked_time' && constraint.metadata?.kind === 'sleep'
              ).length === 0 ? (
                <p className="text-sm text-muted-foreground">No sleep protection configured.</p>
              ) : (
                constraints
                  .filter(
                    constraint =>
                      constraint.constraint_type === 'blocked_time' && constraint.metadata?.kind === 'sleep'
                  )
                  .map(constraint => {
                    const days = constraint.days_of_week
                      .map(day => WEEK_DAYS.find(item => item.value === day)?.label)
                      .filter(Boolean)
                      .join(', ');
                    return (
                      <div
                        key={constraint.id}
                        className="flex min-h-11 items-center gap-3 rounded-md border bg-background p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">
                            {constraint.start_time} - {constraint.end_time}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {days || 'No days'} | non-permeable
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteConstraint(constraint.id)}
                          aria-label="Delete sleep protection"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="h-5 w-5" />
              Vacation Times
            </CardTitle>
            <CardDescription>
              Vacation windows are entirely non-permeable and pull in-window deadlines before the window begins.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="vacationStartDate">Start date</Label>
                <Input
                  id="vacationStartDate"
                  type="date"
                  value={vacationStartDate}
                  onChange={event => setVacationStartDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vacationEndDate">End date</Label>
                <Input
                  id="vacationEndDate"
                  type="date"
                  value={vacationEndDate}
                  onChange={event => setVacationEndDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vacationStartTime">Start time</Label>
                <Input
                  id="vacationStartTime"
                  type="time"
                  value={vacationStartTime}
                  onChange={event => setVacationStartTime(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vacationEndTime">End time</Label>
                <Input
                  id="vacationEndTime"
                  type="time"
                  value={vacationEndTime}
                  onChange={event => setVacationEndTime(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vacationLabel">Label</Label>
                <Input
                  id="vacationLabel"
                  value={vacationLabel}
                  onChange={event => setVacationLabel(event.target.value)}
                  placeholder="Flight, vacation, appointment..."
                />
              </div>
              <div className="flex items-end">
                <Button className="w-full md:w-auto" onClick={addVacationRange} disabled={savingVacation}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {constraints.filter(
                constraint =>
                  constraint.constraint_type === 'blocked_time' && constraint.metadata?.kind === 'vacation'
              ).length === 0 ? (
                <p className="text-sm text-muted-foreground">No vacation ranges scheduled.</p>
              ) : (
                constraints
                  .filter(
                    constraint =>
                      constraint.constraint_type === 'blocked_time' && constraint.metadata?.kind === 'vacation'
                  )
                  .sort((a, b) => String(a.metadata?.date || '').localeCompare(String(b.metadata?.date || '')))
                  .map(constraint => (
                    <div
                      key={constraint.id}
                      className="flex min-h-11 items-center gap-3 rounded-md border bg-background p-3"
                    >
                      <div className="min-w-0 flex-1">
	                        <div className="text-sm font-medium">
	                          {String(constraint.metadata?.label || 'Vacation day')}
                        </div>
	                        <div className="text-xs text-muted-foreground">
	                          {formatVacationConstraintRange(constraint)} - non-permeable
	                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteConstraint(constraint.id)}
	                        aria-label="Delete vacation range"
	                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BriefcaseBusiness className="h-5 w-5" />
              Override Work Ranges
            </CardTitle>
            <CardDescription>
              Add dated work windows for days or ranges that are normally off.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="overrideStartDate">Start date</Label>
                <Input
                  id="overrideStartDate"
                  type="date"
                  value={overrideStartDate}
                  onChange={event => setOverrideStartDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="overrideEndDate">End date</Label>
                <Input
                  id="overrideEndDate"
                  type="date"
                  value={overrideEndDate}
                  onChange={event => setOverrideEndDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="overrideStartTime">Start time</Label>
                <Input
                  id="overrideStartTime"
                  type="time"
                  value={overrideStartTime}
                  onChange={event => setOverrideStartTime(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="overrideEndTime">End time</Label>
                <Input
                  id="overrideEndTime"
                  type="time"
                  value={overrideEndTime}
                  onChange={event => setOverrideEndTime(event.target.value)}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="overrideLabel">Label</Label>
                <Input
                  id="overrideLabel"
                  value={overrideLabel}
                  onChange={event => setOverrideLabel(event.target.value)}
                  placeholder="Saturday sprint, travel catch-up..."
                />
              </div>
              <div className="md:col-span-2">
                <Button className="w-full sm:w-auto" onClick={addOverrideWorkRange} disabled={savingOverride}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add override
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {constraints.filter(
                constraint =>
                  constraint.constraint_type === 'work_hours' && constraint.metadata?.kind === 'override_work_day'
              ).length === 0 ? (
                <p className="text-sm text-muted-foreground">No override work ranges scheduled.</p>
              ) : (
                constraints
                  .filter(
                    constraint =>
                      constraint.constraint_type === 'work_hours' && constraint.metadata?.kind === 'override_work_day'
                  )
                  .sort((a, b) => formatConstraintDateRange(a).localeCompare(formatConstraintDateRange(b)))
                  .map(constraint => (
                    <div
                      key={constraint.id}
                      className="flex min-h-11 items-center gap-3 rounded-md border bg-background p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">
                          {String(constraint.metadata?.label || 'Override work range')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatConstraintDateRange(constraint)} | {constraint.start_time} - {constraint.end_time}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteConstraint(constraint.id)}
                        aria-label="Delete override work range"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" />
              Breaks
            </CardTitle>
            <CardDescription>
              Breaks are protected. Choose semi-permeable only for breaks that pressing due-date matters may use.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-100">
              When a break is created, decide whether it is non-permeable or semi-permeable. The scheduler infringes in this order: semi-permeable breaks, outside working hours, then non-permeable breaks.
            </div>

            <div className="grid gap-3 md:grid-cols-[1fr_1fr_1.3fr_auto]">
              <div className="space-y-2">
                <Label htmlFor="breakStart">Start</Label>
                <Input
                  id="breakStart"
                  type="time"
                  value={breakStart}
                  onChange={event => setBreakStart(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="breakEnd">End</Label>
                <Input
                  id="breakEnd"
                  type="time"
                  value={breakEnd}
                  onChange={event => setBreakEnd(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Permeability</Label>
                <Select value={breakPermeability} onValueChange={value => setBreakPermeability(value as 'semi' | 'non')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non">Non-permeable</SelectItem>
                    <SelectItem value="semi">Semi-permeable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button className="w-full md:w-auto" onClick={addBreak} disabled={savingBreak}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Break days</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-7">
                {WEEK_DAYS.map(day => (
                  <label
                    key={day.value}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-md border bg-background px-3 text-sm"
                  >
                    <Checkbox
                      checked={breakDays.includes(day.value)}
                      onCheckedChange={value => toggleBreakDay(day.value, Boolean(value))}
                    />
                    <span>{day.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {constraints.filter(constraint => constraint.constraint_type === 'break').length === 0 ? (
                <p className="text-sm text-muted-foreground">No breaks configured.</p>
              ) : (
                constraints
                  .filter(constraint => constraint.constraint_type === 'break')
                  .map(constraint => {
                    const permeability = constraint.metadata?.permeability === 'semi' ? 'semi' : 'non';
                    return (
                      <div
                        key={constraint.id}
                        className="flex min-h-11 items-center gap-3 rounded-md border bg-background p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">
                            {constraint.start_time} - {constraint.end_time}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatConstraintDays(constraint.days_of_week)} | {permeability === 'semi' ? 'Semi-permeable' : 'Non-permeable'}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteConstraint(constraint.id)}
                          aria-label="Delete break"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Timezone */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timezone
            </CardTitle>
            <CardDescription>Your local timezone for activity scheduling</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {!TIMEZONE_OPTIONS.some(option => option.value === timezone) && (
                  <SelectItem value={timezone}>{timezone}</SelectItem>
                )}
                {TIMEZONE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Google Calendar Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link className="h-5 w-5" />
              Google Calendar Integration
            </CardTitle>
            <CardDescription>
              Connect your Google Calendar for automatic event syncing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {calendarConnected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span>Calendar connected</span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSyncCalendar} disabled={loadingCalendar}>
                    {loadingCalendar ? 'Syncing...' : 'Sync Now'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDisconnectCalendar}
                    disabled={loadingCalendar}
                  >
                    Disconnect
                  </Button>
                </div>
                <div className="space-y-3 rounded-md border p-3">
                  <div>
                    <h3 className="text-sm font-medium">Calendars used as constraints</h3>
                    <p className="text-muted-foreground text-xs">
                      Selected calendars are displayed on the schedule and treated as immovable time.
                    </p>
                  </div>
                  {calendars.length === 0 ? (
                    <Button variant="outline" onClick={loadCalendars} disabled={loadingCalendar}>
                      Load calendars
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      {calendars.map(calendar => {
                        const checked = selectedCalendarIds.includes(calendar.id);
                        return (
                          <label
                            key={calendar.id}
                            className="flex min-h-11 items-center gap-3 rounded-md border bg-background p-3"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={value => toggleCalendar(calendar.id, Boolean(value))}
                            />
                            <span
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: calendar.color || '#38bdf8' }}
                            />
                            <span className="min-w-0 flex-1 truncate text-sm">
                              {calendar.summary}
                              {calendar.primary ? ' (primary)' : ''}
                            </span>
                          </label>
                        );
                      })}
                      <Button
                        className="w-full sm:w-auto"
                        onClick={saveCalendarSelection}
                        disabled={loadingCalendar || selectedCalendarIds.length === 0}
                      >
                        Save selected calendars
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Button onClick={handleConnectCalendar} disabled={loadingCalendar}>
                {loadingCalendar ? 'Connecting...' : 'Connect Google Calendar'}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Energy Levels */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Energy Preferences
            </CardTitle>
            <CardDescription>Help the AI understand your peak productivity times</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Peak Energy Times</Label>
              <div className="flex flex-wrap gap-2">
                <Badge>Morning (6am-12pm)</Badge>
                <Badge variant="outline">Afternoon (12pm-6pm)</Badge>
                <Badge variant="outline">Evening (6pm-12am)</Badge>
              </div>
              <p className="text-muted-foreground mt-2 text-sm">
                The system learns from your activity completion patterns to optimize scheduling
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSaveSettings} size="lg">
            <Save className="mr-2 h-4 w-4" />
            Save Settings
          </Button>
        </div>
      </div>
      <Dialog open={Boolean(verificationPreview)} onOpenChange={open => !open && cancelEmailVerification()}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Confirm email link</DialogTitle>
            <DialogDescription>
              Confirm before Fluxiplanner attaches this email to your account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 rounded-md border bg-muted/30 p-3 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Email being linked</div>
              <div className="font-medium">{verificationPreview?.email}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Fluxiplanner account</div>
              <div className="font-medium">{verificationPreview?.current_email || primaryEmail}</div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:space-x-0">
            <Button variant="outline" onClick={cancelEmailVerification} disabled={verifyingEmail}>
              Cancel
            </Button>
            <Button onClick={confirmEmailVerification} disabled={verifyingEmail}>
              {verifyingEmail ? 'Linking...' : 'Confirm link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function providerLabel(provider: string): string {
  switch (provider) {
    case 'local':
      return 'Password login';
    case 'verified_email':
      return 'Verified email';
    case 'mail':
      return 'SahastaMail';
    case 'github':
      return 'GitHub';
    case 'zoho':
      return 'Zoho';
    default:
      return provider || 'Linked account';
  }
}

function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
